import React, { useState, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Grid2 } from '@mui/material';
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import TableLoader from '../../../components/common/TableLoader';
import TableSkeleton from '../../../components/common/TableSkeleton';
import NoDataRow from '../../../components/common/NoDataRow';
import StatusChip from '../../../components/Billing/Invoice/InvoiceStatusChip';
import RemarkPopover from '../../../components/Billing/common/RemarkPopover';
import { TABLE_HEADER_ROW_SX } from '../../../utils/typographyStyles';
import { formatDateDMY } from '../../../utils/dateUtils';
import { ReactComponent as PrinterIcon } from '../../../assets/SVG/printerblue.svg';
import { ReactComponent as ViewIcon } from '../../../assets/SVG/eye_view.svg';
import { ReactComponent as SettleBillIcon } from '../../../assets/SVG/receipt-edit_2.svg';
import { ReactComponent as ReceiptIcon } from '../../../assets/SVG/receipt.svg';
import { PharmacyInvoiceReportGen } from '../../../components/Reports/PharmacyInvoice';
import { usePharmacyInvoiceDetails } from './viewPharmacyInvoice.helper';
import PharmacyReceiptDialog from '../../../components/Billing/Pharmacy/PharmacyReceiptDialog';
import { usePharmacyPaymentsQuery } from './pharmacyReceiptHelper';
import { CollectPaymentModal, PaymentDetails, PaymentFormValues, PaymentHistoryItem, buildPaymentApiPayload } from '../collectPayment';
import { useCreatePayment, buildCreatePaymentParams, useBillingConfigOptions } from '../Invoice/newInvoice/newInvoiceForm.helper';
import { useClinic } from '../../../components/ClinicProvider';
import useCurrency from '../../../components/CurrencyProvider';
import { useQueryClient } from '@tanstack/react-query';
import type { PaymentApiPayload } from '../collectPayment/collectPayment.types';
import { storePrevRoute } from '../../../utils/commonUtils';

// ============================================================================
// Types 
// ============================================================================

export interface PharmacyBilledTableData {
  id: number | null;
  patient_id: string | number;
  date: string;
  patientName: string;
  patientAge?: string | number;
  patientMrn?: string;
  patientGender?: string;
  billNo: string;
  totalAmt: string | number;
  concessionAmt: string | number;
  netAmt: string | number;
  paidAmt: string | number;
  balanceAmt: string | number;
  status: string;
  billRemark?: string | null;
}

interface PharmacyBilledTableProps {
  data: PharmacyBilledTableData[];
  loading?: boolean;
  showPatientColumn?: boolean;
}

// ============================================================================
// Constants
// ============================================================================

const TABLE_LABELS = {
  noData: 'No billed records found.',
  loading: 'Loading...',
  date: 'Date',
  patientName: 'Patient Name / MR No.',
  billNo: 'Bill No.',
  totalAmt: 'Total Amt',
  concessionAmt: 'Concession Amt',
  netAmt: 'Net Amt',
  paidAmt: 'Paid Amt',
  balanceAmt: 'Balance Amt',
  status: 'Status',
  billRemark: 'Bill Remark',
  actions: 'Actions',
};

const ACTION_LABELS = {
  view: 'View Invoice',
  print: 'Print Invoice',
  more: 'More actions',
  settle: 'Settle Bill',
  receipt: 'Receipts',
  openRemark: 'Show remark',
};

const getHeaderCells = (currencySymbol: string): Array<{
  key: string;
  label: string;
  minWidth?: number;
  align?: 'center' | 'left' | 'right';
}> => [
  { key: 'date', label: TABLE_LABELS.date, minWidth: 100 },
  { key: 'patientName', label: TABLE_LABELS.patientName, minWidth: 180 },
  { key: 'billNo', label: TABLE_LABELS.billNo, minWidth: 100 },
  { key: 'totalAmt', label: `${TABLE_LABELS.totalAmt} (${currencySymbol})`, minWidth: 90, align: 'right' },
  { key: 'concessionAmt', label: `${TABLE_LABELS.concessionAmt} (${currencySymbol})`, minWidth: 110, align: 'right' },
  { key: 'netAmt', label: `${TABLE_LABELS.netAmt} (${currencySymbol})`, minWidth: 90, align: 'right' },
  { key: 'paidAmt', label: `${TABLE_LABELS.paidAmt} (${currencySymbol})`, minWidth: 90, align: 'right' },
  { key: 'balanceAmt', label: `${TABLE_LABELS.balanceAmt} (${currencySymbol})`, minWidth: 100, align: 'right' },
  { key: 'status', label: TABLE_LABELS.status, minWidth: 110 },
  { key: 'billRemark', label: TABLE_LABELS.billRemark, minWidth: 180 },
  { key: 'actions', label: TABLE_LABELS.actions, minWidth: 100, align: 'center' },
];

const commonCellSx = {
  verticalAlign: 'middle' as const,
  px: 2,
  py: 1.5,
};

// ============================================================================
// Helper: Normalize status for display
// ============================================================================

const normalizePaymentStatus = (status: string): string => {
  const s = (status || '').toLowerCase().replace(/_/g, ' ');
  switch (s) {
  case 'paid':
    return 'Paid';
  case 'unpaid':
    return 'Unpaid';
  case 'partially paid':
  case 'partial':
    return 'Partially Paid';
  default:
    return status || 'Unpaid';
  }
};

// ============================================================================
// Mobile Card Component
// ============================================================================

const PharmacyBilledMobileCard: React.FC<{ row: PharmacyBilledTableData }> = ({ row }) => (
  <Paper sx={{ p: 2, borderRadius: 2, mb: 1, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
      <Box>
        <Typography variant="subtitle2" fontWeight={600}>
          {row.patientName}{row.patientAge ? ` / ${row.patientAge}` : ''}
        </Typography>
        <Typography variant="caption" sx={{ color: '#3B82F6' }}>
          {row.patientMrn}{row.patientMrn && row.patientGender ? ' | ' : ''}{row.patientGender}
        </Typography>
      </Box>
      <StatusChip status={normalizePaymentStatus(row.status)} width={100} height={24} />
    </Box>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
      <Typography variant="body2" color="text.secondary">{row.date ? formatDateDMY(row.date) : '-'}</Typography>
      <Typography variant="body2" fontWeight={500}>Bill: {row.billNo}</Typography>
    </Box>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
      <Typography variant="body2">Net: {row.netAmt}</Typography>
      <Typography variant="body2">Paid: {row.paidAmt}</Typography>
      <Typography variant="body2">Balance: {row.balanceAmt}</Typography>
    </Box>
  </Paper>
);

// ============================================================================
// Main Component
// ============================================================================

function PharmacyBilledTable({ data, loading = false, showPatientColumn = true }: PharmacyBilledTableProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { ClinicId } = useClinic();
  const { currency, currencySymbol } = useCurrency();
  const createPaymentMutation = useCreatePayment();
  const queryClient = useQueryClient();
  const location = useLocation();

  // Generate header cells with currency symbol
  const headerCells = useMemo(() => getHeaderCells(currencySymbol), [currencySymbol]);

  // Menu state
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuRow, setMenuRow] = useState<PharmacyBilledTableData | null>(null);
  const menuOpen = Boolean(menuAnchorEl);

  // Remark popover state
  const [remarkAnchorEl, setRemarkAnchorEl] = useState<null | HTMLElement>(null);
  const [remarkText, setRemarkText] = useState<string>('');
  // Pharmacy report state
  const [pharmacyReportOpen, setPharmacyReportOpen] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<number | null>(null);

  // Pharmacy receipt state
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
  const [receiptInvoiceId, setReceiptInvoiceId] = useState<number | null>(null);
  const [receiptBillNo, setReceiptBillNo] = useState<string | null>(null);
  const [receiptTotalAmount, setReceiptTotalAmount] = useState<number | string>(0);

  // Settle Bill / Collect Payment modal state
  const [collectPaymentOpen, setCollectPaymentOpen] = useState(false);
  const [paymentDetailsForModal, setPaymentDetailsForModal] = useState<PaymentDetails | null>(null);
  const [settlePatientId, setSettlePatientId] = useState<string | number | null>(null);
  const [settleInvoiceId, setSettleInvoiceId] = useState<number | null>(null);

  // Fetch pharmacy invoice details for report
  const { 
    data: pharmacyReportDetails, 
    isLoading: pharmacyReportLoading, 
    error: pharmacyReportError 
  } = usePharmacyInvoiceDetails(selectedInvoiceId ?? undefined);

  // Fetch payment transactions for report
  const { data: pharmacyPaymentsData } = usePharmacyPaymentsQuery({
    invoiceId: selectedInvoiceId ?? 0,
    enabled: pharmacyReportOpen && (selectedInvoiceId ?? 0) > 0,
  });

  // Fetch billing config for payment modes
  const { data: billingConfigData } = useBillingConfigOptions(settlePatientId ?? undefined);

  // Map payment modes from billing config
  const paymentModes = useMemo(() => {
    if (!billingConfigData) return [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const configData = billingConfigData as any;
    const modeofpayment = configData?.modeofpayment;
    if (!modeofpayment?.length) return [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return modeofpayment.map((item: any) => ({
      id: item.id,
      name: item.name,
      code: String(item.code),
    }));
  }, [billingConfigData]);
  // Menu handlers
  const handleMoreClick = (event: React.MouseEvent<HTMLElement>, row: PharmacyBilledTableData) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setMenuRow(row);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuRow(null);
  };

  const handleSettleBill = () => {
    if (!menuRow) return;
    
    // Set patient ID for billing config
    setSettlePatientId(menuRow.patient_id);
    setSettleInvoiceId(menuRow.id ?? null);
    
    // Set payment details for modal
    const balanceAmount = typeof menuRow.balanceAmt === 'string' 
      ? parseFloat(menuRow.balanceAmt.replace(/[^0-9.]/g, '') || '0')
      : Number(menuRow.balanceAmt) || 0;
    
    setPaymentDetailsForModal({
      date: new Date().toLocaleDateString('en-GB'),
      patientName: menuRow.patientName || 'Patient',
      patientId: String(menuRow.patient_id || ''),
      currency: currency || 'INR',
      netAmount: balanceAmount,
    });
    
    setCollectPaymentOpen(true);
    handleMenuClose();
  };

  // Handle payment save from CollectPaymentModal
  const handlePaymentSave = useCallback(
    async (data: PaymentFormValues, history: PaymentHistoryItem[]) => {
      if (!settleInvoiceId || !settlePatientId || !ClinicId) {
        // eslint-disable-next-line no-console
        console.error('Missing invoice, patient, or clinic for payment');
        return;
      }

      const invoiceIdNum = Number(settleInvoiceId);
      const patientIdNum = Number(settlePatientId);
      const clinicIdNum = Number(ClinicId);

      try {
        let payload: PaymentApiPayload;

        if (data.paymentMode === 'credit') {
          payload = {
            invoice_id: invoiceIdNum,
            patient_id: patientIdNum,
            clinic_id: clinicIdNum,
            payment_against_type: 'INVOICE',
            remarks: '',
            payment_details: [],
          };
        } else {
          payload = buildPaymentApiPayload(
            history,
            invoiceIdNum,
            patientIdNum,
            clinicIdNum,
            currency || 'INR',
            'INVOICE',
            ''
          );
        }

        // Call payments API
        await createPaymentMutation.mutateAsync(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          buildCreatePaymentParams(payload as any)
        );

        // Refresh pharmacy-related queries so balances/status are up to date
        queryClient.invalidateQueries({ queryKey: ['pharmacy-billed-invoices'] });
        queryClient.invalidateQueries({ queryKey: ['pharmacyInvoice'] });

        // Local cleanup; CollectPaymentModal will also trigger onClose
        setCollectPaymentOpen(false);
        setPaymentDetailsForModal(null);
        setSettlePatientId(null);
        setSettleInvoiceId(null);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to create payment from pharmacy billed table', error);
        throw error;
      }
    },
    [ClinicId, createPaymentMutation, currency, queryClient, settleInvoiceId, settlePatientId]
  );

  // Close handler for collect payment modal
  const handleCollectPaymentClose = () => {
    setCollectPaymentOpen(false);
    setPaymentDetailsForModal(null);
    setSettlePatientId(null);
    setSettleInvoiceId(null);
  };

  const handleReceipt = () => {
    if (menuRow && menuRow.id) {
      setReceiptInvoiceId(menuRow.id);
      setReceiptBillNo(menuRow.billNo || null);
      setReceiptTotalAmount(menuRow.netAmt || menuRow.totalAmt || 0);
      setReceiptDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleReceiptDialogClose = () => {
    setReceiptDialogOpen(false);
    setReceiptInvoiceId(null);
    setReceiptBillNo(null);
    setReceiptTotalAmount(0);
  };

  // Remark popover handlers
  const handleOpenRemark = (event: React.MouseEvent<HTMLElement>, text: string) => {
    event.stopPropagation();
    setRemarkAnchorEl(event.currentTarget);
    setRemarkText(text);
  };

  const handleCloseRemark = () => {
    setRemarkAnchorEl(null);
    setRemarkText('');
  };

  // Pharmacy report handlers
  const handlePrintInvoice = (invoiceId: number) => {
    setSelectedInvoiceId(invoiceId);
    setPharmacyReportOpen(true);
  };

  const handlePharmacyReportClose = () => {
    setPharmacyReportOpen(false);
    setSelectedInvoiceId(null);
  };

  const visibleHeaderCells = useMemo(
    () => (showPatientColumn ? headerCells : headerCells.filter((h) => h.key !== 'patientName')),
    [showPatientColumn, headerCells]
  );

  // Mobile view
  if (isMobile) {
    return (
      <Box sx={{ width: '100%', p: 1, minHeight: 200, position: 'relative' }}>
        {loading ? (
          <TableLoader />
        ) : data && data.length > 0 ? (
          <Grid2 container spacing={2} sx={{ flexGrow: 1 }}>
            {data.map((row, idx) => (
              <Grid2 key={idx} size={12}>
                <PharmacyBilledMobileCard row={row} />
              </Grid2>
            ))}
          </Grid2>
        ) : (
          <NoDataRow message={TABLE_LABELS.noData} colSpan={4} />
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', overflowX: 'auto', position: 'relative' }}>
      <TableContainer
        component={Paper}
        sx={{ minWidth: 360, boxShadow: 'none', border: 'none' }}
      >
        <Table
          size="small"
          aria-label="pharmacy billed table"
          sx={{
            borderCollapse: 'separate',
            borderSpacing: 0,
            '& th, & td': {
              borderBottom: '1px solid',
              borderColor: '#F0F0F0',
            },
          }}
        >
          <TableHead>
            <TableRow sx={TABLE_HEADER_ROW_SX}>
              {visibleHeaderCells.map((h, index) => (
                <TableCell
                  key={h.key}
                  align={h.align}
                  sx={{
                    ...(h.minWidth ? { minWidth: h.minWidth } : {}),
                    whiteSpace: 'nowrap',
                    px: 2,
                    ...(index === 0 ? { borderTopLeftRadius: 12, borderBottomLeftRadius: 12 } : {}),
                    ...(index === visibleHeaderCells.length - 1 ? { borderTopRightRadius: 12, borderBottomRightRadius: 12 } : {}),
                  }}
                >
                  {h.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableSkeleton
                columnWidths={showPatientColumn
                  ? [100, 180, 100, 90, 110, 90, 90, 100, 110, 180, 100]
                  : [100, 100, 90, 110, 90, 90, 100, 110, 180, 100]
                }
                rows={10}
              />
            ) : data && data.length > 0 ? (
              data.map((row, idx) => {
                const status = normalizePaymentStatus(row.status).toLowerCase();
                const showMenu = status === 'paid' || status === 'unpaid' || status === 'partially paid';

                return (
                  <TableRow
                    key={idx}
                    hover
                    sx={{
                      height: 56,
                      '&:hover': { backgroundColor: 'rgba(0,0,0,0.02)' },
                    }}
                  >
                    <TableCell sx={{ ...commonCellSx, minWidth: 100, whiteSpace: 'nowrap' }}>
                      {row.date ? formatDateDMY(row.date) : '-'}
                    </TableCell>
                    {showPatientColumn && (
                      <TableCell sx={{ ...commonCellSx, minWidth: 180 }}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: '#232323' }}>
                            {row.patientName}{row.patientAge ? ` / ${row.patientAge}` : ''}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#3B82F6',
                              display: 'block',
                              mt: 0.25,
                            }}
                          >
                            {row.patientMrn}{row.patientMrn && row.patientGender ? ' | ' : ''}{row.patientGender}
                          </Typography>
                        </Box>
                      </TableCell>
                    )}
                    <TableCell sx={commonCellSx}>{row.billNo || '-'}</TableCell>
                    <TableCell align="right" sx={commonCellSx}>{row.totalAmt}</TableCell>
                    <TableCell align="right" sx={commonCellSx}>{row.concessionAmt}</TableCell>
                    <TableCell align="right" sx={commonCellSx}>{row.netAmt}</TableCell>
                    <TableCell align="right" sx={commonCellSx}>{row.paidAmt}</TableCell>
                    <TableCell align="right" sx={commonCellSx}>{row.balanceAmt}</TableCell>
                    <TableCell sx={commonCellSx}>
                      <StatusChip status={normalizePaymentStatus(row.status)} width={100} height={24} sx={{ fontSize: 12 }} />
                    </TableCell>
                    <TableCell sx={commonCellSx}>
                      {row.billRemark && row.billRemark.trim().length > 0 ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, maxWidth: 200 }}>
                          <Tooltip title={ACTION_LABELS.openRemark} arrow>
                            <IconButton
                              size="small"
                              sx={{ p: 0, color: '#5A8AEA', flexShrink: 0 }}
                              aria-label={ACTION_LABELS.openRemark}
                              onClick={(e) => handleOpenRemark(e, row.billRemark || '')}
                            >
                              <InfoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Typography
                            variant="body2"
                            sx={{
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: 160,
                            }}
                          >
                            {row.billRemark}
                          </Typography>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', justifyContent: 'center', width: '30%' }}>-</Box>
                      )}
                    </TableCell>
                    <TableCell align="center" sx={commonCellSx}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        <Tooltip title={ACTION_LABELS.view} arrow>
                          <IconButton
                            size="small"
                            aria-label={ACTION_LABELS.view}
                            onClick={() => {
                              storePrevRoute(location.pathname, location.search);
                              
                              navigate(`/admin/billing/pharmacy/view/${row.patient_id ?? 1710}`, {
                                state: { invoice_id: row.id ?? null },
                              });
                            }}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={ACTION_LABELS.print} arrow>
                          <IconButton 
                            size="small" 
                            aria-label={ACTION_LABELS.print}
                            onClick={() => row.id && handlePrintInvoice(row.id)}
                            disabled={!row.id}
                          >
                            <PrinterIcon />
                          </IconButton>
                        </Tooltip>
                        {showMenu && (
                          <Tooltip title={ACTION_LABELS.more} arrow>
                            <IconButton
                              size="small"
                              aria-label={ACTION_LABELS.more}
                              onClick={(e) => handleMoreClick(e, row)}
                            >
                              <MoreVertOutlinedIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <NoDataRow message={TABLE_LABELS.noData} colSpan={visibleHeaderCells.length} />
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Row actions menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { minWidth: 180, borderRadius: 2 } }}
      >
        {menuRow && normalizePaymentStatus(menuRow.status).toLowerCase() !== 'paid' && (
          <MenuItem onClick={handleSettleBill}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SettleBillIcon />
              <span>{ACTION_LABELS.settle}</span>
            </Box>
          </MenuItem>
        )}
        {menuRow && normalizePaymentStatus(menuRow.status).toLowerCase() === 'paid' && (
          <MenuItem onClick={handleReceipt}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ReceiptIcon />
              <span>{ACTION_LABELS.receipt}</span>
            </Box>
          </MenuItem>
        )}
      </Menu>

      {/* Remark popover */}
      <RemarkPopover
        anchorEl={remarkAnchorEl}
        open={Boolean(remarkAnchorEl)}
        content={remarkText}
        onClose={handleCloseRemark}
      />

      {/* Pharmacy Invoice Report Dialog */}
      <PharmacyInvoiceReportGen
        open={pharmacyReportOpen}
        onClose={handlePharmacyReportClose}
        data={pharmacyReportDetails ?? null}
        isLoading={pharmacyReportLoading}
        error={pharmacyReportError?.message ?? null}
        transactions={pharmacyPaymentsData?.transactions ?? []}
      />

      {/* Pharmacy Receipt Dialog */}
      {receiptInvoiceId && (
        <PharmacyReceiptDialog
          open={receiptDialogOpen}
          onClose={handleReceiptDialogClose}
          invoiceId={receiptInvoiceId}
          billNo={receiptBillNo}
          totalBillAmount={receiptTotalAmount}
        />
      )}

      {/* Collect Payment Modal (Settle Bill) */}
      {paymentDetailsForModal && (
        <CollectPaymentModal
          open={collectPaymentOpen}
          onClose={handleCollectPaymentClose}
          onSave={handlePaymentSave}
          paymentDetails={paymentDetailsForModal}
          existingPaymentHistory={[]}
          paymentModes={paymentModes}
          showWallet={false}
          invoiceId={settleInvoiceId ?? undefined}
          isSubmitting={createPaymentMutation.isPending}
        />
      )}
    </Box>
  );
}

export default PharmacyBilledTable;
